import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

interface MultiImageUploadProps {
  chateauId: string;
  onImagesUploaded: (imageUrls: string[]) => void;
  maxFiles?: number;
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  url?: string;
  error?: string;
}

export const MultiImageUpload = ({ chateauId, onImagesUploaded, maxFiles = 10 }: MultiImageUploadProps) => {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const uploadFile = async (file: File, index: number): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${chateauId}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('chateau-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('chateau-images')
      .getPublicUrl(data.path);

    // Update progress to completed
    setUploads(prev => prev.map((upload, i) => 
      i === index 
        ? { ...upload, progress: 100, status: 'completed' as const, url: publicUrl }
        : upload
    ));

    return publicUrl;
  };

  const saveImageToDatabase = async (imageUrl: string, originalName: string) => {
    const { error } = await supabase
      .from('chateau_images')
      .insert({
        chateau_id: chateauId,
        image_url: imageUrl,
        alt_text: `Image ${originalName}`,
        is_primary: false,
        display_order: 0
      });

    if (error) throw error;
  };

  const processFiles = async (files: File[]) => {
    setIsUploading(true);
    
    // Initialize upload states
    const initialUploads: UploadProgress[] = files.map(file => ({
      file,
      progress: 0,
      status: 'uploading' as const
    }));
    setUploads(initialUploads);

    const uploadedUrls: string[] = [];

    try {
      // Upload files sequentially to avoid overwhelming the server
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          // Simulate progress
          const progressInterval = setInterval(() => {
            setUploads(prev => prev.map((upload, index) => 
              index === i && upload.progress < 90 
                ? { ...upload, progress: upload.progress + 10 }
                : upload
            ));
          }, 100);

          const imageUrl = await uploadFile(file, i);
          await saveImageToDatabase(imageUrl, file.name);
          
          clearInterval(progressInterval);
          uploadedUrls.push(imageUrl);
          
        } catch (error) {
          console.error('Erreur upload fichier:', error);
          setUploads(prev => prev.map((upload, index) => 
            index === i 
              ? { 
                  ...upload, 
                  status: 'error' as const, 
                  error: error instanceof Error ? error.message : 'Erreur inconnue' 
                }
              : upload
          ));
        }
      }

      if (uploadedUrls.length > 0) {
        toast({
          title: "Images uploadées avec succès",
          description: `${uploadedUrls.length} image(s) ajoutée(s) au château.`,
        });
        onImagesUploaded(uploadedUrls);
      }

    } catch (error) {
      toast({
        title: "Erreur lors de l'upload",
        description: error instanceof Error ? error.message : "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Clear uploads after 3 seconds
      setTimeout(() => setUploads([]), 3000);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    if (acceptedFiles.length > maxFiles) {
      toast({
        title: "Trop de fichiers",
        description: `Vous ne pouvez uploader que ${maxFiles} fichiers à la fois.`,
        variant: "destructive",
      });
      return;
    }

    // Validate file types and sizes
    const validFiles = acceptedFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Type de fichier non supporté",
          description: `${file.name} n'est pas une image.`,
          variant: "destructive",
        });
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        toast({
          title: "Fichier trop volumineux",
          description: `${file.name} dépasse 5MB.`,
          variant: "destructive",
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      processFiles(validFiles);
    }
  }, [chateauId, maxFiles, onImagesUploaded, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    },
    multiple: true,
    maxFiles,
    disabled: isUploading
  });

  const removeUpload = (index: number) => {
    setUploads(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-border hover:border-primary/50'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        
        {isDragActive ? (
          <p className="text-primary font-medium">Déposez les images ici...</p>
        ) : (
          <div>
            <p className="text-foreground font-medium mb-2">
              Glissez-déposez des images ou cliquez pour sélectionner
            </p>
            <p className="text-sm text-muted-foreground">
              Formats supportés: JPG, PNG, GIF, WebP (max {maxFiles} fichiers, 5MB chacun)
            </p>
            <Button type="button" variant="outline" className="mt-4" disabled={isUploading}>
              <Upload className="w-4 h-4 mr-2" />
              Sélectionner les images
            </Button>
          </div>
        )}
      </div>

      {uploads.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium">Uploads en cours ({uploads.length})</h4>
          {uploads.map((upload, index) => (
            <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium truncate">{upload.file.name}</span>
                  <div className="flex items-center gap-2">
                    {upload.status === 'completed' && (
                      <span className="text-xs text-green-600 font-medium">✓ Terminé</span>
                    )}
                    {upload.status === 'error' && (
                      <span className="text-xs text-red-600 font-medium">✗ Erreur</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUpload(index)}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                {upload.status === 'uploading' && (
                  <Progress value={upload.progress} className="h-1" />
                )}
                
                {upload.error && (
                  <p className="text-xs text-red-600 mt-1">{upload.error}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};